import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { studentAPI, videoAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { BookOpen, Video, FileText, Calendar, LogOut, Loader2, Home, Play, AlertTriangle, MessageCircle, Clock, Lock, Radio, VideoIcon, X, Download, Globe } from 'lucide-react';
import JitsiMeet from '../components/JitsiMeet';

const WHATSAPP_NUMBER = '+16478745620';

const StudentDashboard = () => {
  const { language, toggleLanguage } = useLanguage();
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expiredCourses, setExpiredCourses] = useState([]);
  const [liveSession, setLiveSession] = useState(null);
  const [joiningSession, setJoiningSession] = useState(false);
  const [activeLiveSessions, setActiveLiveSessions] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [loadingRecordings, setLoadingRecordings] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // overview, recordings, materials
  const [videoModal, setVideoModal] = useState({ open: false, recording: null });

  const content = {
    en: {
      title: "Student Portal",
      welcome: "Welcome",
      enrolledCourses: "My Courses",
      upcomingSessions: "Upcoming Sessions",
      liveSessions: "Live Now",
      recordings: "Recordings",
      materials: "Materials",
      noCourses: "You are not enrolled in any courses yet.",
      noSessions: "No upcoming sessions",
      noLive: "No live sessions right now",
      noRecordings: "No recordings available yet",
      joinClass: "Join Class",
      watchRecording: "Watch Recording",
      downloadRecording: "Download",
      recordedOn: "Recorded on",
      duration: "Duration",
      teacher: "Teacher",
      course: "Course",
      overview: "Overview",
      contactModerator: "Contact us to enroll in courses",
      logout: "Logout",
      backToHome: "Back to Home",
      subscriptionExpired: "Your subscription has expired",
      courseExpired: "Course Expired",
      expiredOn: "Expired on",
      renewSubscription: "To renew your subscription, please contact customer support via WhatsApp",
      contactWhatsApp: "Contact on WhatsApp",
      expiresOn: "Expires",
      active: "Active"
    },
    ar: {
      title: "بوابة الطالب",
      welcome: "مرحباً",
      enrolledCourses: "دوراتي",
      upcomingSessions: "الحصص القادمة",
      liveSessions: "مباشر الآن",
      recordings: "التسجيلات",
      materials: "المواد",
      noCourses: "أنت غير مسجل في أي دورة حتى الآن.",
      noSessions: "لا توجد حصص قادمة",
      noLive: "لا توجد حصص مباشرة الآن",
      noRecordings: "لا توجد تسجيلات متاحة بعد",
      joinClass: "انضم للحصة",
      watchRecording: "مشاهدة التسجيل",
      downloadRecording: "تحميل",
      recordedOn: "تم التسجيل في",
      duration: "المدة",
      teacher: "المعلم",
      course: "الدورة",
      overview: "نظرة عامة",
      contactModerator: "تواصل معنا للتسجيل في الدورات",
      logout: "تسجيل الخروج",
      backToHome: "العودة للرئيسية",
      subscriptionExpired: "انتهى اشتراكك",
      courseExpired: "الدورة منتهية",
      expiredOn: "انتهى في",
      renewSubscription: "لتجديد اشتراكك، يرجى التواصل مع خدمة العملاء عبر الواتساب",
      contactWhatsApp: "تواصل عبر الواتساب",
      expiresOn: "ينتهي في",
      active: "نشط"
    }
  };

  const t = content[language];

  // Check if a date is expired
  const isExpired = (dateString) => {
    if (!dateString) return false;
    return new Date(dateString) < new Date();
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Generate WhatsApp link with pre-filled message
  const getWhatsAppLink = () => {
    const message = language === 'ar' 
      ? `مرحباً، أود تجديد اشتراكي في الدورة. اسمي: ${user?.name}, البريد: ${user?.email}`
      : `Hello, I would like to renew my course subscription. Name: ${user?.name}, Email: ${user?.email}`;
    return `https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}?text=${encodeURIComponent(message)}`;
  };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }

    if (!authLoading && user && user.role !== 'student') {
      navigate(`/${user.role}`);
      return;
    }

    const fetchDashboard = async () => {
      try {
        const response = await studentAPI.getDashboard();
        setDashboard(response.data);
        
        // Check for expired courses from profile
        if (response.data?.profile?.subscription_end) {
          const subEnd = new Date(response.data.profile.subscription_end);
          if (subEnd < new Date()) {
            setExpiredCourses(response.data.profile.enrolled_courses || []);
          }
        }
        
        // Fetch active live sessions
        try {
          const activeResponse = await videoAPI.getActiveSessions();
          setActiveLiveSessions(activeResponse.data.active_sessions || []);
        } catch (e) {
          console.log('Could not fetch active sessions:', e);
        }
        
        // Fetch recordings
        try {
          const recordingsResponse = await studentAPI.getRecordings();
          setRecordings(recordingsResponse.data.recordings || []);
        } catch (e) {
          console.log('Could not fetch recordings:', e);
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    if (user && user.role === 'student') {
      fetchDashboard();
    }
  }, [user, authLoading, navigate]);

  // Fetch recordings when switching to recordings tab
  const fetchRecordings = async () => {
    setLoadingRecordings(true);
    try {
      const response = await studentAPI.getRecordings();
      setRecordings(response.data.recordings || []);
    } catch (err) {
      console.error('Failed to fetch recordings:', err);
    } finally {
      setLoadingRecordings(false);
    }
  };

  // Join a live class
  const handleJoinLiveClass = async (sessionId, sessionTitle) => {
    setJoiningSession(true);
    try {
      const response = await videoAPI.joinSession(sessionId);
      setLiveSession({
        ...response.data,
        sessionTitle: sessionTitle || 'Live Class'
      });
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to join live class');
    } finally {
      setJoiningSession(false);
    }
  };

  // Leave live class
  const handleLeaveLiveClass = () => {
    setLiveSession(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => navigate('/login')}>Go to Login</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t.title}</h1>
            <p className="text-gray-600">{t.welcome}, {user?.name}</p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={toggleLanguage}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <Globe className="w-4 h-4" />
              <span>{language === 'en' ? 'العربية' : 'English'}</span>
            </Button>
            <Link to="/" className="text-gray-600 hover:text-teal-600 flex items-center gap-2">
              <Home className="w-5 h-5" />
              {t.backToHome}
            </Link>
            <Button variant="outline" onClick={handleLogout} className="flex items-center gap-2">
              <LogOut className="w-4 h-4" />
              {t.logout}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Expired Subscription Alert */}
        {dashboard?.profile?.subscription_end && isExpired(dashboard.profile.subscription_end) && (
          <div className="mb-8 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex items-start gap-4">
              <div className="bg-white/20 rounded-full p-3">
                <AlertTriangle className="w-8 h-8" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold mb-2">{t.subscriptionExpired}</h2>
                <p className="text-white/90 mb-1">
                  {t.expiredOn}: {formatDate(dashboard.profile.subscription_end)}
                </p>
                <p className="text-white/80 text-sm mb-4">{t.renewSubscription}</p>
                <a
                  href={getWhatsAppLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                  {t.contactWhatsApp}
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Live Sessions Alert */}
        {dashboard?.live_sessions?.length > 0 && (
          <div className="mb-8 bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl p-6 text-white">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
              <h2 className="text-xl font-bold">{t.liveSessions}</h2>
            </div>
            <div className="grid gap-4">
              {dashboard.live_sessions.map((session) => (
                <div key={session.id} className="bg-white/20 rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">{session.title}</h3>
                    <p className="text-sm opacity-90">{session.course?.title?.[language] || session.course?.title}</p>
                  </div>
                  <Button
                    onClick={() => handleJoinLiveClass(session.session_id || session.id, session.title)}
                    disabled={joiningSession}
                    className="bg-white text-red-600 px-4 py-2 rounded-lg font-semibold flex items-center gap-2 hover:bg-gray-100 transition-colors"
                    data-testid={`join-live-${session.id}`}
                  >
                    {joiningSession ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                    {t.joinClass}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active Live Sessions from Video API */}
        {activeLiveSessions.length > 0 && !dashboard?.live_sessions?.length && (
          <div className="mb-8 bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl p-6 text-white">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
              <h2 className="text-xl font-bold">{t.liveSessions}</h2>
            </div>
            <div className="grid gap-4">
              {activeLiveSessions.map((session) => (
                <div key={session.id} className="bg-white/20 rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">{session.teacher_name}'s Class</h3>
                    <p className="text-sm opacity-90 flex items-center gap-2">
                      <Radio className="w-3 h-3 animate-pulse" />
                      Live Now
                    </p>
                  </div>
                  <Button
                    onClick={() => handleJoinLiveClass(session.session_id, `${session.teacher_name}'s Class`)}
                    disabled={joiningSession}
                    className="bg-white text-red-600 px-4 py-2 rounded-lg font-semibold flex items-center gap-2 hover:bg-gray-100 transition-colors"
                  >
                    {joiningSession ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <VideoIcon className="w-4 h-4" />
                    )}
                    {t.joinClass}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div 
            className={`bg-white rounded-xl p-6 shadow-sm cursor-pointer transition-all hover:shadow-md ${activeTab === 'overview' ? 'ring-2 ring-teal-500' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <BookOpen className="w-8 h-8 text-teal-600 mb-3" />
            <h3 className="text-2xl font-bold text-gray-900">{dashboard?.total_enrolled || 0}</h3>
            <p className="text-gray-600">{t.enrolledCourses}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <Calendar className="w-8 h-8 text-blue-600 mb-3" />
            <h3 className="text-2xl font-bold text-gray-900">{dashboard?.upcoming_sessions?.length || 0}</h3>
            <p className="text-gray-600">{t.upcomingSessions}</p>
          </div>
          <div 
            className={`bg-white rounded-xl p-6 shadow-sm cursor-pointer transition-all hover:shadow-md ${activeTab === 'recordings' ? 'ring-2 ring-purple-500' : ''}`}
            onClick={() => { setActiveTab('recordings'); fetchRecordings(); }}
          >
            <Video className="w-8 h-8 text-purple-600 mb-3" />
            <h3 className="text-2xl font-bold text-gray-900">{recordings.length || 0}</h3>
            <p className="text-gray-600">{t.recordings}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <FileText className="w-8 h-8 text-amber-600 mb-3" />
            <h3 className="text-2xl font-bold text-gray-900">{dashboard?.live_sessions?.length || 0}</h3>
            <p className="text-gray-600">{t.liveSessions}</p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'overview' ? 'default' : 'outline'}
            onClick={() => setActiveTab('overview')}
            className={activeTab === 'overview' ? 'bg-teal-600 hover:bg-teal-700' : ''}
          >
            <BookOpen className="w-4 h-4 mr-2" />
            {t.overview}
          </Button>
          <Button
            variant={activeTab === 'recordings' ? 'default' : 'outline'}
            onClick={() => { setActiveTab('recordings'); fetchRecordings(); }}
            className={activeTab === 'recordings' ? 'bg-purple-600 hover:bg-purple-700' : ''}
          >
            <Video className="w-4 h-4 mr-2" />
            {t.recordings} ({recordings.length})
          </Button>
        </div>

        {/* Recordings Tab */}
        {activeTab === 'recordings' && (
          <div className="bg-white rounded-xl p-6 shadow-sm mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Video className="w-6 h-6 text-purple-600" />
              {t.recordings}
            </h2>
            
            {loadingRecordings ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
              </div>
            ) : recordings.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {recordings.map((recording) => (
                  <div 
                    key={recording.id} 
                    className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow group"
                  >
                    {/* Recording Thumbnail */}
                    <div 
                      className="relative h-40 bg-gradient-to-br from-purple-600 to-purple-800 flex items-center justify-center cursor-pointer"
                      onClick={() => setVideoModal({ open: true, recording })}
                    >
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
                      <Play className="w-16 h-16 text-white/90 group-hover:scale-110 transition-transform" />
                      {recording.duration_minutes && (
                        <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                          {recording.duration_minutes} min
                        </span>
                      )}
                    </div>
                    
                    {/* Recording Info */}
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-1 line-clamp-1">
                        {language === 'ar' && recording.title_ar ? recording.title_ar : recording.title}
                      </h3>
                      <p className="text-sm text-gray-500 mb-2 flex items-center gap-1">
                        <BookOpen className="w-3 h-3" />
                        {language === 'ar' && recording.course?.title_ar ? recording.course.title_ar : recording.course?.title}
                      </p>
                      {recording.teacher?.name && (
                        <p className="text-xs text-gray-400 mb-3">
                          {t.teacher}: {recording.teacher.name}
                        </p>
                      )}
                      {recording.scheduled_time && (
                        <p className="text-xs text-gray-400 mb-3">
                          {t.recordedOn}: {new Date(recording.scheduled_time).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US')}
                        </p>
                      )}
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="flex-1 bg-purple-600 hover:bg-purple-700"
                          onClick={() => setVideoModal({ open: true, recording })}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          {t.watchRecording}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const url = recording.recording_url.startsWith('http') 
                              ? recording.recording_url 
                              : `${process.env.REACT_APP_BACKEND_URL}${recording.recording_url}`;
                            window.open(url, '_blank');
                          }}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Video className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>{t.noRecordings}</p>
              </div>
            )}
          </div>
        )}

        {/* Overview Tab - Enrolled Courses */}
        {activeTab === 'overview' && (
          <>
          <div className="bg-white rounded-xl p-6 shadow-sm mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">{t.enrolledCourses}</h2>
          {dashboard?.enrolled_courses?.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dashboard.enrolled_courses.map((course) => {
                const courseExpired = dashboard?.profile?.subscription_end && isExpired(dashboard.profile.subscription_end);
                
                return (
                  <div 
                    key={course.id} 
                    className={`border rounded-lg p-4 transition-colors relative ${
                      courseExpired 
                        ? 'border-red-200 bg-red-50 opacity-75' 
                        : 'hover:border-teal-500'
                    }`}
                  >
                    {/* Expired overlay */}
                    {courseExpired && (
                      <div className="absolute top-2 right-2">
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 bg-red-100 px-2 py-1 rounded-full">
                          <Lock className="w-3 h-3" />
                          {t.courseExpired}
                        </span>
                      </div>
                    )}
                    
                    <h3 className="font-semibold text-gray-900 mb-2 pr-20">{course.title?.[language] || course.title}</h3>
                    <p className="text-sm text-gray-600 mb-3">{course.description?.[language]?.substring(0, 100)}...</p>
                    
                    {course.teacher && (
                      <p className="text-sm text-teal-600 mb-2">Teacher: {course.teacher.name}</p>
                    )}
                    
                    {/* Subscription status */}
                    {dashboard?.profile?.subscription_end && (
                      <div className={`text-xs mt-2 pt-2 border-t ${courseExpired ? 'border-red-200' : 'border-gray-100'}`}>
                        {courseExpired ? (
                          <span className="text-red-600 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            {t.expiredOn}: {formatDate(dashboard.profile.subscription_end)}
                          </span>
                        ) : (
                          <span className="text-green-600 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {t.expiresOn}: {formatDate(dashboard.profile.subscription_end)}
                          </span>
                        )}
                      </div>
                    )}
                    
                    {/* WhatsApp renew button for expired courses */}
                    {courseExpired && (
                      <a
                        href={getWhatsAppLink()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        <MessageCircle className="w-4 h-4" />
                        {t.contactWhatsApp}
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{t.noCourses}</p>
              <p className="text-sm mt-2">{t.contactModerator}</p>
            </div>
          )}
        </div>

        {/* Upcoming Sessions */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-xl font-bold text-gray-900 mb-4">{t.upcomingSessions}</h2>
          {dashboard?.upcoming_sessions?.length > 0 ? (
            <div className="space-y-3">
              {dashboard.upcoming_sessions.map((session) => (
                <div key={session.id} className="border rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">{session.title}</h3>
                    <p className="text-sm text-gray-600">{session.course?.title?.[language]}</p>
                    <p className="text-sm text-teal-600">
                      {new Date(session.scheduled_time).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US')}
                    </p>
                  </div>
                  {session.zoom_link_active && (
                    <a
                      href={session.zoom_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-teal-600 text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 hover:bg-teal-700 transition-colors"
                    >
                      <Play className="w-4 h-4" />
                      {t.joinClass}
                    </a>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{t.noSessions}</p>
            </div>
          )}
        </div>
        </>
        )}
      </main>

      {/* Video Player Modal */}
      <Dialog open={videoModal.open} onOpenChange={(open) => setVideoModal({ open, recording: null })}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Video className="w-5 h-5 text-purple-600" />
                {videoModal.recording && (language === 'ar' && videoModal.recording.title_ar ? videoModal.recording.title_ar : videoModal.recording.title)}
              </span>
            </DialogTitle>
          </DialogHeader>
          
          {videoModal.recording && (
            <div className="p-4">
              {/* Video Player */}
              <div className="bg-black rounded-lg overflow-hidden aspect-video">
                <video
                  controls
                  autoPlay
                  className="w-full h-full"
                  src={
                    videoModal.recording.recording_url?.startsWith('http') 
                      ? videoModal.recording.recording_url 
                      : `${process.env.REACT_APP_BACKEND_URL}${videoModal.recording.recording_url}`
                  }
                >
                  Your browser does not support the video tag.
                </video>
              </div>
              
              {/* Recording Details */}
              <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                <div className="flex items-center gap-4">
                  {videoModal.recording.course && (
                    <span className="flex items-center gap-1">
                      <BookOpen className="w-4 h-4" />
                      {language === 'ar' && videoModal.recording.course.title_ar ? videoModal.recording.course.title_ar : videoModal.recording.course.title}
                    </span>
                  )}
                  {videoModal.recording.teacher?.name && (
                    <span>
                      {t.teacher}: {videoModal.recording.teacher.name}
                    </span>
                  )}
                </div>
                {videoModal.recording.scheduled_time && (
                  <span>
                    {t.recordedOn}: {new Date(videoModal.recording.scheduled_time).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US')}
                  </span>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Jitsi Live Class Modal */}
      {liveSession && (
        <JitsiMeet
          roomName={liveSession.room_name}
          domain={liveSession.jitsi_domain}
          config={liveSession.config}
          userInfo={{
            name: user?.name || 'Student',
            email: user?.email
          }}
          isTeacher={false}
          sessionTitle={liveSession.sessionTitle}
          onClose={handleLeaveLiveClass}
        />
      )}
    </div>
  );
};

export default StudentDashboard;
